import * as z from 'zod/mini'
import type { PublicAnnouncement } from '../../types/interfaces/common-interfaces'
import { keyedIssue } from '../../errors/validation-keys'
import { PUBLIC_SLOTS_MAX_RANGE_DAYS } from '../../business/misc/public-booking'
import { PUBLIC_EVENTS_MAX_RANGE_DAYS, rangeDays } from '../../business/misc/public-events'
import { isoDateTime, queryBoolean, queryNumber } from '../../schemas/common'

/**
 * `GET entities?search=` - a premise name, description or id. Required and at least three characters, so
 * the public endpoint is a lookup and never a dump of every premise.
 */
export const getEntitiesQuery = z.object({
  search: z.string().check(z.trim(), z.minLength(3), z.maxLength(200)),
})
export type GetEntitiesQuery = z.infer<typeof getEntitiesQuery>

/**
 * A premise's public events in a window. Both bounds are instants (`toISOString`) and both are
 * optional: no `start` is now, no `end` is `PUBLIC_EVENTS_MAX_RANGE_DAYS` after the start
 * (`resolvePublicEventsRange`). Given both, they may span at most that many days. The API then cuts
 * the range to `publicEventsWindow`.
 */
export const getPublicEntityEventsQuery = z
  .object({
    start: z.optional(isoDateTime),
    end: z.optional(isoDateTime),
  })
  .check(
    z.superRefine((value, ctx) => {
      if (!value.start || !value.end) {
        return
      }
      const range = { start: new Date(value.start), end: new Date(value.end) }
      if (range.end < range.start) {
        ctx.addIssue(keyedIssue('validation.endAfterStart', ['end']))
      } else if (rangeDays(range) > PUBLIC_EVENTS_MAX_RANGE_DAYS) {
        ctx.addIssue(keyedIssue('validation.eventRangeTooLong', ['end']))
      }
    }),
  )
export type GetPublicEntityEventsQuery = z.infer<typeof getPublicEntityEventsQuery>

/**
 * Bookable slots. The widget always asks by `blueprintId`; `activityId` is the older way in and
 * still accepted. At least one of the two is required, reported on `blueprintId`.
 *
 * One request covers at most `PUBLIC_SLOTS_MAX_RANGE_DAYS` between `start` and `end`. `summary=true`
 * adds the availability summary over the whole booking horizon (`ReservationAvailabilitySummary`).
 */
export const getPublicEntityReservationSlotsQuery = z
  .object({
    activityId: z.optional(z.uuid()),
    blueprintId: z.optional(z.uuid()),
    duration: z.optional(queryNumber(z.int().check(z.positive()))),
    start: z.optional(isoDateTime),
    end: z.optional(isoDateTime),
    summary: z.optional(queryBoolean()),
  })
  .check(
    z.superRefine((value, ctx) => {
      if (!value.activityId && !value.blueprintId) {
        ctx.addIssue(keyedIssue('validation.blueprintOrActivityRequired', ['blueprintId']))
      }
      if (value.start && value.end) {
        const days = (new Date(value.end).getTime() - new Date(value.start).getTime()) / 86_400_000
        if (days > PUBLIC_SLOTS_MAX_RANGE_DAYS) {
          ctx.addIssue(keyedIssue('validation.slotRangeTooLong', ['end']))
        }
      }
    }),
  )
export type GetPublicEntityReservationSlotsQuery = z.infer<typeof getPublicEntityReservationSlotsQuery>

/** `GET entities/:entityId/announcement` - for `<seiri-announcement>`. `null` while nothing is live. */
export interface GetEntityAnnouncementResponse {
  announcement: PublicAnnouncement | null
}
